import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { BP } from "../../utils/breakpoints.js";
import { initSectionThemeTriggers } from "../../utils/sectionThemeTriggers.js";
import { initLogoVideo } from "../../utils/logoVideo.js";
import { lockScroll, unlockScroll } from "../../utils/Scroll.js";
import { openModalBackdrop, closeModalBackdrop } from "../../utils/ModalBackdrop.js";

const OPEN_WIDTH = 192;
const CLOSE_SCALE = 1.5;

export default class Navigation {
  constructor(el) {
    this.el = el;
    this.toggle = el.querySelector("[data-nav-toggle]");
    this.dropdown = el.querySelector(".c-navigation__dropdown");
    this.menuLabel = el.querySelector(".c-navigation__toggle-label-menu");
    this.closeLabel = el.querySelector(".c-navigation__toggle-label-close");
    this.menuItems = [...el.querySelectorAll(".c-menu__items > *")];
    this.menuSearch = el.querySelector(".c-menu__search");
    this.exploreBtn = el.querySelector("[data-explore-toggle]");
    this.subnav = el.querySelector(".c-menu__subnav");

    this.isOpen = false;
    this.exploreOpen = false;
    this._tl = null;

    this.setup();
    this.init();
    initLogoVideo(this.el.querySelector(".c-navigation__logo"));
  }

  setup() {
    this.allItems = [...this.menuItems, this.menuSearch].filter(Boolean);
    this.allItems.forEach((item, i) => gsap.set(item, { zIndex: this.allItems.length - i }));

    this._setClosedState();
  }

  // Layout-dependent measurements (toggle width, items' stacked offsets) differ
  // between mobile and desktop, so they are re-taken whenever the layout changes.
  _measure() {
    gsap.set(this.toggle, { clearProps: "width" });
    this.initialToggleWidth = this.toggle.getBoundingClientRect().width;

    if (!this.allItems.length) return;

    gsap.set(this.allItems, { clearProps: "transform" });
    const firstRect = this.menuItems[0].getBoundingClientRect();
    this.itemInitialY = this.allItems.map((item) => {
      const rect = item.getBoundingClientRect();
      return -(rect.top - firstRect.top) - rect.height;
    });
  }

  // Snaps everything to the closed state without animating.
  _setClosedState() {
    if (this._tl) this._tl.kill();
    this._tl = null;

    this._measure();

    gsap.set(this.dropdown, { autoAlpha: 0, pointerEvents: "none", y: -8 });
    gsap.set(this.menuLabel, { clearProps: "transform" });
    gsap.set(this.closeLabel, { y: "100%" });
    this.allItems.forEach((item, i) => gsap.set(item, { x: 0, y: this.itemInitialY[i] }));
  }

  _onResize() {
    // Height-only resizes (mobile URL bar, virtual keyboard on the search field)
    // must not close the menu.
    const width = window.innerWidth;
    if (width === this._lastWidth) return;
    this._lastWidth = width;

    if (this.isOpen) this.close({ immediate: true });

    clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(() => {
      if (!this.isOpen) this._setClosedState();
    }, 150);
  }

  init() {
    this.toggle.addEventListener("click", () => this.onToggle());

    if (this.exploreBtn) {
      this.exploreBtn.addEventListener("click", () => this.onExploreToggle());
    }

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen) this.close();
    });

    document.addEventListener("click", (e) => {
      if (this.isOpen && !this.el.contains(e.target)) this.close();
    });

    // Any modal / panel opening (auth, newsletter, cart…) closes the menu.
    window.addEventListener("modalbackdrop:overlay-open", (e) => {
      if (this.isOpen && e.detail.key !== this) this.close();
    });

    this._onScroll();
    window.addEventListener("scroll", () => this._onScroll(), { passive: true });

    this._lastWidth = window.innerWidth;
    window.addEventListener("resize", () => this._onResize());

    this._initThemeTriggers();

    const menuEl = this.el.querySelector(".c-menu");
    const hoverTargets = [...this.el.querySelectorAll(".c-menu__item, .c-menu__explore-group, .c-menu__search")];

    hoverTargets.forEach((item) => {
      item.addEventListener("mouseenter", () => {
        menuEl.classList.add("is-item-focused");
        if (item.matches(".c-menu__item, .c-menu__explore-group") && this._isDesktop()) {
          gsap.to(item, { x: 10, duration: 0.44, ease: "jitterSmooth", overwrite: "auto" });
        }
      });
      item.addEventListener("mouseleave", () => {
        if (item.matches(".c-menu__item, .c-menu__explore-group")) {
          gsap.to(item, { x: 0, duration: 0.44, ease: "jitterSmooth", overwrite: "auto" });
        }
        requestAnimationFrame(() => {
          if (!menuEl.querySelector(".c-menu__item:hover, .c-menu__explore-group:hover, .c-menu__search:hover")) {
            menuEl.classList.remove("is-item-focused");
          }
        });
      });
    });
  }

  _initThemeTriggers() {
    initSectionThemeTriggers(this.el);

    const mainContent = document.querySelector(".main-content");
    if (mainContent) {
      // Batched: the content's height changes many times while images, videos
      // and fonts load, and each refresh re-measures every trigger (forced
      // layouts). One refresh once it has settled for a moment.
      let refreshTimer = null;
      new ResizeObserver(() => {
        clearTimeout(refreshTimer);
        refreshTimer = setTimeout(() => ScrollTrigger.refresh(), 150);
      }).observe(mainContent);
    }
  }

  _onScroll() {
    const scrolled = window.scrollY > window.innerHeight * 0.5;
    this.el.classList.toggle("is-scrolled", scrolled);
  }

  onToggle() {
    this.isOpen ? this.close() : this.open();
  }

  onExploreToggle() {
    this.exploreOpen ? this.closeExplore() : this.openExplore();
  }

  _isDesktop() {
    return window.matchMedia(BP.desktop).matches;
  }

  open() {
    if (this._tl) this._tl.kill();

    this.isOpen = true;
    this.el.classList.remove("is-closing");
    this.el.classList.add("is-open");
    this.dropdown.setAttribute("aria-hidden", "false");

    // Mobile: page pixelated behind the menu, nav kept live above it. Scroll is
    // locked so the page doesn't move under its frozen capture.
    if (!this._isDesktop()) {
      this.coversPage = true;
      openModalBackdrop(this, { keep: ".c-navigation" });
      lockScroll();
    }

    this._tl = gsap
      .timeline()
      .to(this.dropdown, { autoAlpha: 1, pointerEvents: "auto", y: 0, duration: 0.25, ease: "power2.out" }, 0)
      .to(this.menuLabel, { y: "-100%", duration: 0.4, ease: "jitterSmooth" }, 0)
      .to(this.allItems, { y: 0, duration: 0.68, ease: "jitterSmooth" }, 0)
      .to(this.closeLabel, { y: "0%", duration: 0.44, ease: "jitterSmooth" }, 0);

    if (this._isDesktop()) {
      this._tl.to(this.toggle, { width: OPEN_WIDTH, duration: 0.4, ease: "jitterSmooth" }, 0);
    }
  }

  close({ immediate = false } = {}) {
    if (this._tl) this._tl.kill();

    this.isOpen = false;
    this.el.classList.remove("is-open");
    this.el.classList.add("is-closing");
    this.dropdown.setAttribute("aria-hidden", "true");

    if (this.coversPage) {
      this.coversPage = false;
      unlockScroll();
      closeModalBackdrop(this);
    }

    if (immediate) {
      this.el.classList.remove("is-closing");
      this._setClosedState();
      this.closeExplore();
      return;
    }

    this._tl = gsap
      .timeline({
        onComplete: () => {
          this.el.classList.remove("is-closing");
          gsap.set(this.toggle, { clearProps: "width" });
          gsap.set(this.menuLabel, { clearProps: "transform" });
        },
      })
      .to(this.dropdown, { autoAlpha: 0, pointerEvents: "none", y: -8, duration: 0.25 / CLOSE_SCALE, ease: "power2.in" }, 0)
      .to(this.menuLabel, { y: "0%", duration: 0.4 / CLOSE_SCALE, ease: "jitterSmooth" }, 0)
      .to(this.allItems, { y: (i) => this.itemInitialY[i], duration: 0.68 / CLOSE_SCALE, ease: "jitterSmooth" }, 0)
      .to(this.closeLabel, { y: "100%", duration: 0.44 / CLOSE_SCALE, ease: "jitterSmooth" }, 0);

    if (this._isDesktop()) {
      this._tl.to(this.toggle, { width: this.initialToggleWidth, duration: 0.4 / CLOSE_SCALE, ease: "jitterSmooth" }, 0);
    }

    this.closeExplore();
  }

  openExplore() {
    this.exploreOpen = true;
    this.exploreBtn.classList.add("is-open");
    this.subnav.classList.add("is-open");
    this.subnav.setAttribute("aria-hidden", "false");
  }

  closeExplore() {
    this.exploreOpen = false;
    this.exploreBtn.classList.remove("is-open");
    this.subnav.classList.remove("is-open");
    this.subnav.setAttribute("aria-hidden", "true");
  }
}
